/**
 * Overview sheet for "Download All": every roster row's finished composite
 * (with its <SIZE>-<qty> header) laid out side by side, one line per size,
 * NOT nested — the factory's own "all sizes at a glance" sheet.
 *
 * Composites are inlined as `<g transform="translate() scale()">` copies of
 * their inner markup rather than nested `<svg>` viewports (CorelDRAW's
 * importer drops nested viewports), with every id namespaced per copy so
 * clipPaths/patterns can't collide.
 */
import { namespaceSvgIds } from './logoVectorize';

export interface OverviewEntry {
  size: string;
  /** Finished composite SVG (physical pt width/height + viewBox on the root). */
  svg: string;
}

export interface OverviewLayout {
  /** Gap between composites and around the sheet, as a fraction of the tallest composite. */
  gapFraction?: number;
  /**
   * Optional size label at the start of each line, as a fraction of the
   * tallest composite. Off by default: every composite already carries its
   * own "<SIZE>-<qty>" header, like the factory's reference sheet.
   */
  rowLabelFraction?: number;
}

interface ParsedComposite {
  widthPt: number;
  heightPt: number;
  viewBox: [number, number, number, number];
  inner: string;
}

function parseComposite(svg: string): ParsedComposite | null {
  const open = svg.match(/<svg\b[^>]*>/i);
  if (!open || open.index === undefined) return null;
  const tag = open[0];
  const widthPt = Number(tag.match(/\swidth="([\d.]+)"/i)?.[1]);
  const heightPt = Number(tag.match(/\sheight="([\d.]+)"/i)?.[1]);
  const vb = tag
    .match(/viewBox="([^"]+)"/i)?.[1]
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  const close = svg.lastIndexOf('</svg>');
  if (!vb || vb.length !== 4 || vb.some((n) => !Number.isFinite(n)) || vb[2] <= 0 || vb[3] <= 0) {
    return null;
  }
  if (!Number.isFinite(widthPt) || !Number.isFinite(heightPt) || widthPt <= 0 || heightPt <= 0) {
    return null;
  }
  if (close <= open.index + tag.length) return null;
  return {
    widthPt,
    heightPt,
    viewBox: [vb[0], vb[1], vb[2], vb[3]],
    inner: svg.slice(open.index + tag.length, close),
  };
}

const r = (n: number) => Math.round(n * 1000) / 1000;
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Build the overview sheet. Rows keep roster order and are grouped by size
 * (first appearance decides the line order); each line starts with the size
 * name and holds that size's composites left to right. Physical units: pt.
 */
export function buildOverviewSheet(entries: OverviewEntry[], layout: OverviewLayout = {}): string | null {
  const parsed = entries
    .map((e) => ({ size: e.size, composite: parseComposite(e.svg) }))
    .filter((e): e is { size: string; composite: ParsedComposite } => !!e.composite);
  if (!parsed.length) return null;

  const tallest = Math.max(...parsed.map((e) => e.composite.heightPt));
  const gap = tallest * (layout.gapFraction ?? 0.04);
  const rowLabelPt = tallest * (layout.rowLabelFraction ?? 0);

  // Group by size, preserving first-seen order.
  const lines: { size: string; items: ParsedComposite[] }[] = [];
  for (const e of parsed) {
    const line = lines.find((l) => l.size === e.size);
    if (line) line.items.push(e.composite);
    else lines.push({ size: e.size, items: [e.composite] });
  }

  let y = gap;
  let sheetWidth = 0;
  const parts: string[] = [];
  let copy = 0;
  for (const line of lines) {
    const lineHeight = Math.max(...line.items.map((c) => c.heightPt));
    let x = gap;
    if (rowLabelPt > 0) {
      parts.push(
        `<text x="${r(x)}" y="${r(y + rowLabelPt)}" font-family="Arial, Helvetica, sans-serif"` +
          ` font-weight="bold" font-size="${r(rowLabelPt)}" fill="#4d4d4d" xml:space="preserve">` +
          `${esc(line.size.toUpperCase())}</text>`
      );
      y += rowLabelPt * 1.3;
    }
    for (const c of line.items) {
      const s = c.widthPt / c.viewBox[2]; // pt per composite unit
      const tx = x - c.viewBox[0] * s;
      const ty = y - c.viewBox[1] * s;
      copy += 1;
      parts.push(
        `<g transform="translate(${r(tx)} ${r(ty)}) scale(${r(s)})">` +
          namespaceSvgIds(c.inner, `ov${copy}x`) +
          '</g>'
      );
      x += c.widthPt + gap;
    }
    sheetWidth = Math.max(sheetWidth, x);
    y += lineHeight + gap;
  }
  const sheetHeight = y;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1"` +
    ` width="${r(sheetWidth)}" height="${r(sheetHeight)}" viewBox="0 0 ${r(sheetWidth)} ${r(sheetHeight)}">` +
    parts.join('') +
    '</svg>'
  );
}
